import React, { useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { useClientBadges, useRemoveCancellation, useShippedInTime } from '@/hooks/client';
import { ClientBadgesModal } from '@/types/modal';
import { ErrorModal, LoadingModal, NoFooterModal } from '@/components/molecules/common/Modal';
import Table from '@/components/molecules/common/Table';
import styles from '../Modal.module.scss';
import tableStyles from '../../molecules/common/Table.module.scss';
import { ClientBadgesOverview, ClientBadgesProductDetail, ClientBadgesShippingDetail } from '@/types/client';
import { transformDateString } from '@/utilities/datetime';
import { Stack, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RemoveCancellationModal from './RemoveCancellationModal';
import ShippedInTimeModal from './ShippedInTimeModal';
import { useErrorHandling } from '@/hooks/common';

type OverviewCellType = 'qualityRate' | 'authenticationRate' | 'shippingRate' | 'recentSoldProducts';
type ProductDetailCellType = 'notCompliant' | 'counterfeit' | 'notReceived';

type ProductDetailRowType = ClientBadgesProductDetail & { id: number };

const defaultPageSize = 10;

const parseRate = (value?: string | null) => parseFloat(value ?? '') || 0;

const checkHighlightOverviewCell = (cell: OverviewCellType, data: ClientBadgesOverview) => {
  const qualityRate = parseRate(data.qualityRate);
  const authenticationRate = parseRate(data.authenticationRate);
  const shippingRate = parseRate(data.shippingRate);
  const recentSoldProducts = parseRate(data.recentSoldProducts);

  if (!data.currentBadgeName || data.currentBadgeName === 'Common') {
    switch (cell) {
      case 'qualityRate':
        return qualityRate < 80;
      case 'authenticationRate':
        return authenticationRate < 100;
      case 'shippingRate':
        return shippingRate < 80;
      case 'recentSoldProducts':
        return recentSoldProducts < 2;
      default:
        return false;
    }
  }
  if (data.currentBadgeName === 'Trusted') {
    switch (cell) {
      case 'qualityRate':
        return qualityRate < 90;
      case 'authenticationRate':
        return authenticationRate < 100;
      case 'shippingRate':
        return shippingRate < 90;
      case 'recentSoldProducts':
        return recentSoldProducts < 5;
      default:
        return false;
    }
  }
};

const checkHighlightProductDetailCell = (cell: ProductDetailCellType, data: ProductDetailRowType) => {
  if (data.id === 0) return false; // id = index

  const notCompliant = parseRate(data.notCompliant);
  const counterfeit = parseRate(data.counterfeit);
  const notReceived = parseRate(data.notReceived);

  switch (cell) {
    case 'notCompliant':
      return notCompliant === 1;
    case 'counterfeit':
      return counterfeit === 1;
    case 'notReceived':
      return notReceived === 1;
    default:
      return false;
  }
};

const View: React.FC<{ modal: ClientBadgesModal }> = ({ modal }) => {
  const { t } = useTranslation();

  const [productDetailsPage, setProductDetailsPage] = useState(1);
  const [productDetailsPageSize, setProductDetailsPageSize] = useState(defaultPageSize);

  const [shippingDetailsPage, setShippingDetailsPage] = useState(1);
  const [shippingDetailsPageSize, setShippingDetailsPageSize] = useState(defaultPageSize);

  const [removeCancelItem, setRemoveCancelItem] = useState<number>();

  const [shippedInTimeItem, setShippedInTimeItem] = useState<number>();

  const [submitError, setSubmitError] = useState('');

  const { client, onClose } = modal;
  const detailR = useClientBadges(Number(client.clientId));

  const { getErrorMsgFromResponse } = useErrorHandling();

  const { mutate: removeCancellation, isLoading: isSubmittingRemoveCanc } = useRemoveCancellation();

  const { mutate: markShippedInTime, isLoading: isSubmittingShippedInTime } = useShippedInTime();

  const onProductDetailsPageChange = ({ page, pageSize }: GridPaginationModel) => {
    setProductDetailsPage(page + 1);
    setProductDetailsPageSize(pageSize);
  };

  const onShippingDetailsPageChange = ({ page, pageSize }: GridPaginationModel) => {
    setShippingDetailsPage(page + 1);
    setShippingDetailsPageSize(pageSize);
  };

  if (detailR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.client_badges.title')}
        onClose={onClose}
      />
    );
  }

  if (detailR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.client_badges.title')}
        error={detailR.error}
        onClose={onClose}
      />
    );
  }

  function overview(data: ClientBadgesOverview): JSX.Element {
    function displayPercent(value: unknown): string {
      if (value === undefined || value == '' || value == null) {
        value = '0';
      }
      return String(value) + ' %';
    }

    const columns: GridColDef[] = [
      {
        field: 'clientId',
        headerName: t('modal.client_badges.overview.fields.client_id'),
        cellClassName: 'grey-background-color',
        renderCell: () => client.clientId.toString(),
      },
      {
        field: 'currentBadgeName',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.current_badge'),
      },
      {
        field: 'currentBadgeId',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.badge_id'),
      },
      {
        field: 'qualityRate',
        headerName: t('modal.client_badges.overview.fields.quality_rate'),
        cellClassName: clsx('grey-background-color', {
          [styles.clientBadges_highlight]: checkHighlightOverviewCell('qualityRate', data),
        }),
        renderCell: (params: GridCellParams) => displayPercent(params.value),
      },
      {
        field: 'authenticationRate',
        cellClassName: clsx('grey-background-color', {
          [styles.clientBadges_highlight]: checkHighlightOverviewCell('authenticationRate', data),
        }),
        headerName: t('modal.client_badges.overview.fields.authentication_rate'),
        renderCell: (params: GridCellParams) => displayPercent(params.value),
      },
      {
        field: 'litigations',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.litigations'),
      },
      {
        field: 'cancellationOnTime',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.cancellation_on_time'),
      },
      {
        field: 'totalCancellation',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.total_cancellation'),
      },
      {
        field: 'shippedOnTime',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.shipped_on_time'),
      },
      {
        field: 'totalShipped',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.total_shipped'),
      },
      {
        field: 'recentSoldProducts',
        cellClassName: clsx('grey-background-color', {
          [styles.clientBadges_highlight]: checkHighlightOverviewCell('recentSoldProducts', data),
        }),
        headerName: t('modal.client_badges.overview.fields.recent_sold_products'),
      },
      {
        field: 'isExcluded',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.is_excluded'),
      },
      {
        field: 'shippingRate',
        cellClassName: clsx('grey-background-color', {
          [styles.clientBadges_highlight]: checkHighlightOverviewCell('shippingRate', data),
        }),
        renderHeader: () => (
          <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <span className={tableStyles.headerCellSmallText}>
              {t('modal.client_badges.overview.fields.shipping_rate')}{' '}
            </span>
            <Tooltip
              title={
                <Stack gap={0.5}>
                  <span>{t('modal.client_badges.overview.shipping_rate_description')}</span>
                  <span>{`(${data.shippedOnTime} + ${data.litigations}) / (${data.totalShipped} + ${data.litigations} + ${data.totalCancellation}) = ${data.shippingRate}%`}</span>
                </Stack>
              }
              classes={{ tooltip: styles.clientBadges_tooltipDescription }}
            >
              <InfoIcon sx={{ width: 14, height: 14 }} />
            </Tooltip>
          </Stack>
        ),
        renderCell: (params: GridCellParams) => displayPercent(params.value),
      },
      {
        field: 'updatedAt',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.overview.fields.last_update'),
        renderCell: (params: GridCellParams) => transformDateString(String(params.value)),
      },
    ];

    return (
      <div>
        <div className={clsx([styles.text1rem, styles.textBold])}>Badge overview</div>
        <div className={styles.height1rem} />
        <Table
          sx={{
            '& .grey-background-color': {
              backgroundColor: '#eeeeee',
            },
          }}
          rows={[{ ...data, id: 0 }]}
          columns={columns}
          table={{
            kind: 'Table',
          }}
          autoHeight={false}
          shouldNotPaddingColumnHeader={true}
          shouldUseSmallHeaderText={true}
        />
      </div>
    );
  }

  function returnTotalRowColVal(index: number, page: number, value: unknown): string {
    if (index != 0 || page != 1) {
      return `<div>` + value + `</div>`;
    }

    if (value == '' || value === undefined) {
      value = 'X';
    }

    return `<div style="font-weight: bold">` + value + `</div>`;
  }

  const renderProductDetailActionCell = (params: GridCellParams<ProductDetailRowType>) => {
    if (Number(params.id) === 0) return <span style={{ fontWeight: 'bold' }}>0</span>;

    const { notReceivedRestored, notReceived, productId } = params.row;

    if (!notReceived) return <span>N/A</span>;

    const notReceivedValue = Number(notReceived);

    if (notReceivedValue === 0 && notReceivedRestored) {
      return Number(notReceivedRestored) === 1 ? (
        <span>{t('modal.client_badges.product_details.remove_cancellation.received_restored')}</span>
      ) : (
        <span>{t('common.na')}</span>
      );
    }

    return notReceivedValue === 1 ? (
      <button
        className={styles.clientBadges_actionLink}
        onClick={() => (productId ? setRemoveCancelItem(Number(productId)) : undefined)}
      >
        {t('modal.client_badges.product_details.remove_cancellation.not_received')}
      </button>
    ) : (
      <span>{t('common.na')}</span>
    );
  };

  const renderShippingDetailActionCell = (params: GridCellParams<ClientBadgesShippingDetail>) => {
    if (Number(params.id) === 0) return <span style={{ fontWeight: 'bold' }}>0</span>;

    const { shippedInTime, productId } = params.row;

    if (shippedInTime == null) return <span>{t('common.na')}</span>;

    return Number(shippedInTime) === 0 ? (
      <button
        className={styles.clientBadges_actionLink}
        onClick={() => (productId ? setShippedInTimeItem(Number(productId)) : undefined)}
      >
        {t('modal.client_badges.product_details.shipped_in_time.shipped_in_time_action')}
      </button>
    ) : (
      <span>{t('common.na')}</span>
    );
  };

  function productDetails(
    details: ClientBadgesProductDetail[],
    totalProductDetail: ClientBadgesProductDetail,
    pageSize: number,
    page: number,
    onPageChange: (model: GridPaginationModel) => void,
  ): JSX.Element {
    const total = details.length;

    const start = (page - 1) * pageSize;
    const end = page * pageSize;

    const rows: ProductDetailRowType[] = [totalProductDetail, ...details].slice(start, end).map((row, index) => ({
      id: index,
      ...row,
    }));

    const columns: GridColDef[] = [
      {
        field: 'productId',
        headerName: t('modal.client_badges.product_details.fields.product_id'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'removedFromSale',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.product_details.fields.removed_from_sale'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'compliant',
        headerName: t('modal.client_badges.product_details.fields.compliant'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'notCompliant',
        headerClassName: 'grey-background-color',
        cellClassName: (d) =>
          clsx('grey-background-color', {
            [styles.clientBadges_highlight]: checkHighlightProductDetailCell('notCompliant', d.row),
          }),
        headerName: t('modal.client_badges.product_details.fields.not_compliant'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'counterfeit',
        headerName: t('modal.client_badges.product_details.fields.counterfeit'),
        cellClassName: (d) =>
          clsx({
            [styles.clientBadges_highlight]: checkHighlightProductDetailCell('counterfeit', d.row),
          }),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'received',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.product_details.fields.received'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'shipped',
        headerName: t('modal.client_badges.product_details.fields.shipped'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'notReceived',
        headerClassName: 'grey-background-color',
        cellClassName: (d) =>
          clsx('grey-background-color', {
            [styles.clientBadges_highlight]: checkHighlightProductDetailCell('notReceived', d.row),
          }),
        headerName: t('modal.client_badges.product_details.fields.not_received'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'receivedDate',
        headerName: t('modal.client_badges.product_details.fields.received_date'),
        renderCell: (params: GridCellParams) => (
          <div
            dangerouslySetInnerHTML={{
              __html: returnTotalRowColVal(
                Number(params.id),
                page,
                String(
                  transformDateString(String(params.value))
                    ? transformDateString(String(params.value))
                    : String(params.value) == '' ||
                        params.value == undefined ||
                        params.value == null ||
                        String(params.value) === '0000-00-00 00:00:00'
                      ? ''
                      : String(params.value),
                ),
              ),
            }}
          />
        ),
      },
      {
        field: 'isVip',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.product_details.fields.is_vip'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'segment',
        headerName: t('modal.client_badges.product_details.fields.segment'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'action',
        headerName: t('modal.client_badges.product_details.fields.action'),
        renderCell: renderProductDetailActionCell,
      },
    ];

    return (
      <div>
        <div className={clsx([styles.text1rem, styles.textBold])}>
          {t('modal.client_badges.product_details.header')}
        </div>
        <div className={styles.height1rem} />
        <Table
          sx={{
            '& .grey-background-color': {
              backgroundColor: '#eeeeee',
            },
          }}
          rows={rows}
          columns={columns}
          table={{
            kind: 'DynamicTable',
            totalItem: total,
            pageSize,
            pageSizeOptions: [defaultPageSize, 50, 100],
            onPageChange,
          }}
          shouldNotPaddingColumnHeader={true}
          shouldUseSmallHeaderText={true}
        />
      </div>
    );
  }

  function shippingDetails(
    details: ClientBadgesShippingDetail[],
    totalShippingDetail: ClientBadgesShippingDetail,
    pageSize: number,
    page: number,
    onPageChange: (model: GridPaginationModel) => void,
  ): JSX.Element {
    const total = details.length;

    const start = (page - 1) * pageSize;
    const end = page * pageSize;

    const rows = [totalShippingDetail, ...details].slice(start, end).map((row, index) => ({
      id: index,
      ...row,
    }));

    const columns: GridColDef[] = [
      {
        field: 'productId',
        headerName: t('modal.client_badges.shipping_details.fields.product_id'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'productName',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.shipping_details.fields.product_name'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'soldDate',
        headerName: t('modal.client_badges.shipping_details.fields.sold_date'),
        renderCell: (params: GridCellParams) => (
          <div
            dangerouslySetInnerHTML={{
              __html: returnTotalRowColVal(
                Number(params.id),
                page,
                String(
                  transformDateString(String(params.value))
                    ? transformDateString(String(params.value))
                    : String(params.value) == '' ||
                        params.value == undefined ||
                        params.value == null ||
                        String(params.value) === '0000-00-00 00:00:00'
                      ? ''
                      : String(params.value),
                ),
              ),
            }}
          />
        ),
      },
      {
        field: 'pickupDate',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.shipping_details.fields.pickup_date'),
        renderCell: (params: GridCellParams) => (
          <div
            dangerouslySetInnerHTML={{
              __html: returnTotalRowColVal(
                Number(params.id),
                page,
                String(
                  transformDateString(String(params.value))
                    ? transformDateString(String(params.value))
                    : String(params.value) == '' ||
                        params.value == undefined ||
                        params.value == null ||
                        String(params.value) === '0000-00-00 00:00:00'
                      ? ''
                      : String(params.value),
                ),
              ),
            }}
          />
        ),
      },
      {
        field: 'shippedInTime',
        headerName: t('modal.client_badges.shipping_details.fields.shipped_in_time'),
        cellClassName: (d) => {
          return clsx({
            [styles.clientBadges_highlight]: d.row?.id !== 0 && parseRate(d.row?.shippedInTime) === 0,
          });
        },
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'daysToShip',
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        headerName: t('modal.client_badges.shipping_details.fields.days_to_ship'),
        renderCell: (params: GridCellParams) => (
          <div
            dangerouslySetInnerHTML={{
              __html: returnTotalRowColVal(Number(params.id), page, params.value),
            }}
          />
        ),
      },
      {
        field: 'directShipping',
        headerName: t('modal.client_badges.shipping_details.fields.direct_shipping'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        headerClassName: 'grey-background-color',
        cellClassName: 'grey-background-color',
        field: 'shippingType',
        headerName: t('modal.client_badges.shipping_details.fields.shipping_type'),
        renderCell: (params: GridCellParams) => (
          <div dangerouslySetInnerHTML={{ __html: returnTotalRowColVal(Number(params.id), page, params.value) }} />
        ),
      },
      {
        field: 'action',
        headerName: t('modal.client_badges.product_details.fields.action'),
        renderCell: renderShippingDetailActionCell,
      },
    ];

    return (
      <div>
        <div className={clsx([styles.text1rem, styles.textBold])}>
          {t('modal.client_badges.shipping_details.header')}
        </div>
        <div className={styles.height1rem} />
        <Table
          sx={{
            '& .grey-background-color': {
              backgroundColor: '#eeeeee',
            },
          }}
          rows={rows}
          columns={columns}
          table={{
            kind: 'DynamicTable',
            totalItem: total,
            pageSize,
            pageSizeOptions: [defaultPageSize, 50, 100],
            onPageChange,
          }}
          shouldNotPaddingColumnHeader={true}
          shouldUseSmallHeaderText={true}
        />
      </div>
    );
  }

  const closeRemoveCancelModal = () => {
    setRemoveCancelItem(undefined);
    setSubmitError('');
  };

  const closeShippedInTimeModal = () => {
    setShippedInTimeItem(undefined);
    setSubmitError('');
  };

  const submitRemoveCanc = () => {
    if (!removeCancelItem) return;
    removeCancellation(
      { productId: removeCancelItem, clientId: Number(client.clientId) },
      {
        onSuccess: () => {
          closeRemoveCancelModal();
          detailR.refetch();
        },
        onError: (err) => {
          setSubmitError(getErrorMsgFromResponse(err) || 'Unknown error');
        },
      },
    );
  };

  const submitShippedInTime = () => {
    if (!shippedInTimeItem) return;
    markShippedInTime(
      { productId: shippedInTimeItem, clientId: Number(client.clientId) },
      {
        onSuccess: () => {
          closeShippedInTimeModal();
          detailR.refetch();
        },
        onError: (err) => {
          setSubmitError(getErrorMsgFromResponse(err) || 'Unknown error');
        },
      },
    );
  };

  if (detailR.data.overview == null || detailR.data.productTotal == null || detailR.data.shippingTotal == null) {
    return (
      <ErrorModal
        title={t('modal.client_badges.title')}
        onClose={onClose}
      />
    );
  }

  const body = (
    <div className={clsx(styles.containerGap)}>
      {overview(detailR.data.overview)}
      {productDetails(
        detailR.data.productDetails ?? [],
        detailR.data.productTotal,
        productDetailsPageSize,
        productDetailsPage,
        onProductDetailsPageChange,
      )}
      {shippingDetails(
        detailR.data.shippingDetails ?? [],
        detailR.data.shippingTotal,
        shippingDetailsPageSize,
        shippingDetailsPage,
        onShippingDetailsPageChange,
      )}
    </div>
  );

  return (
    <>
      <NoFooterModal
        title={t('modal.client_badges.title')}
        onClose={onClose}
        body={body}
        containerStyle={{ width: 'calc(100% - 4rem)' }}
        modalName="client_badges"
      />

      {!!removeCancelItem && (
        <RemoveCancellationModal
          onClose={closeRemoveCancelModal}
          onSubmit={submitRemoveCanc}
          isLoading={isSubmittingRemoveCanc}
          productId={removeCancelItem}
          error={submitError}
        />
      )}

      {!!shippedInTimeItem && (
        <ShippedInTimeModal
          onClose={closeShippedInTimeModal}
          onSubmit={submitShippedInTime}
          isLoading={isSubmittingShippedInTime}
          productId={shippedInTimeItem}
          error={submitError}
        />
      )}
    </>
  );
};

export default View;
